import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, FormHelperText, MenuItem, Switch, Autocomplete, Tooltip, Typography, CircularProgress } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { addClientDocument, updateClientDocument, getClientDocumentScopes, getClientDocumentTypes, getClients, addClientDocumentChange, updateClientDocumentChange } from '../redux/ducks/client';
import { restoreAttachments } from '../utils/uploadUtils';
import { addDays, addMonthsToDay, formatDate, formatDayShort, toLocalDay } from '../utils/dateUtils';
import { useHasPermission } from '../utils/permissionUtils';
import { getApiUrl } from '../config';
import AppDatePicker from './AppDatePicker';
import FilePondUploader from './FilePondUploader';

export const DOCUMENT_TYPES = ['Service Agreement', 'Non-Disclosure Agreement', 'Credit Card Authorization'];

const DOCUMENT_FILE_TYPES = ['application/pdf', 'image/*'];

export const isRecordOnlyDocument = (type) => /credit[\s-]*card/i.test(type || '');

export const documentFileUrl = (clientId, docId, download = false) => `${getApiUrl()}/client/${clientId}/documents/${docId}/file${download ? '?download=1' : ''}`;

export const documentScopeLabel = (scope) => (scope?.type ? (scope.name ? `${scope.type}: ${scope.name}` : scope.type) : '');

export const documentSignerName = (doc) => doc?.signedBy?.fullName || doc?.signedByName || '';

export const documentChangeFileUrl = (clientId, docId, changeId, download = false) =>
    `${getApiUrl()}/client/${clientId}/documents/${docId}/changes/${changeId}/file${download ? '?download=1' : ''}`;

export const EXPIRY_ACTIONS = [
    { value: 'Renews', label: 'Renews automatically' },
    { value: 'Renew', label: 'Needs renewal' },
    { value: 'Cancel', label: 'Needs cancelling' },
    { value: 'Replace', label: 'Needs replacing' },
];

export const expiryActionLabel = (value) => EXPIRY_ACTIONS.find((a) => a.value === value)?.label || '';

const AUTO_RENEW_ACTION = 'Renews';

export const RENEWAL_PERIODS = [
    { value: 1, label: '1 month' },
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 12, label: '12 months' },
];

const renewalPeriodLabel = (months) => RENEWAL_PERIODS.find((p) => p.value === Number(months))?.label || '';

const scheduledRenewal = (doc, today) => {
    const expires = formatDate(doc?.expiresDate);
    const months = Number(doc?.renewalMonths);
    if (!expires || doc?.expiryAction !== AUTO_RENEW_ACTION || !renewalPeriodLabel(months)) return null;
    if (expires >= today) return expires;
    const elapsed = (Number(today.slice(0, 4)) - Number(expires.slice(0, 4))) * 12 + Number(today.slice(5, 7)) - Number(expires.slice(5, 7));
    const periods = Math.floor(elapsed / months) * months;
    const candidate = addMonthsToDay(expires, periods);
    return candidate >= today ? candidate : addMonthsToDay(expires, periods + months);
};

export const documentCancelState = (doc, today = toLocalDay()) => {
    const cancelled = formatDate(doc?.cancelledDate);
    if (!cancelled) return null;
    return cancelled <= today ? 'cancelled' : 'pending';
};

export const isDocumentCancelled = (doc, today = toLocalDay()) => documentCancelState(doc, today) === 'cancelled';

const isRenewalCancelled = (doc, renewal, today) => {
    const state = documentCancelState(doc, today);
    return state === 'cancelled' || (state === 'pending' && formatDate(doc.cancelledDate) <= renewal);
};

export const documentNextRenewal = (doc, today = toLocalDay()) => {
    const renewal = scheduledRenewal(doc, today);
    return renewal && !isRenewalCancelled(doc, renewal, today) ? renewal : null;
};

export const DocumentCancelChip = ({ document }) => {
    const state = documentCancelState(document);
    if (!state) return null;
    if (state === 'pending') return <Chip size="small" color="warning" variant="outlined" label={`Cancels on ${formatDayShort(document.cancelledDate)}`} />;
    return (
        <Tooltip title={`Cancelled on ${formatDate(document.cancelledDate)}`}>
            <Chip size="small" label="Cancelled" />
        </Tooltip>
    );
};

export const documentResponsibleName = (doc) => (doc?.responsible && typeof doc.responsible === 'object' ? doc.responsible.fullName || '' : '');

export const EXPIRING_SOON_DAYS = 30;

export const documentExpiryState = (doc, today = toLocalDay()) => {
    const renewal = scheduledRenewal(doc, today);
    if (renewal && isRenewalCancelled(doc, renewal, today)) return null;
    const expires = renewal || formatDate(doc?.expiresDate);
    if (!expires) return null;
    if (expires < today) return 'expired';
    if (expires <= addDays(today, EXPIRING_SOON_DAYS)) return 'soon';
    return 'later';
};

export const splitDocumentChanges = (doc, today = toLocalDay()) => {
    const changes = (Array.isArray(doc?.changes) ? doc.changes : [])
        .filter((c) => c && formatDate(c.effectiveDate))
        .sort((a, b) => (formatDate(a.effectiveDate) < formatDate(b.effectiveDate) ? -1 : formatDate(a.effectiveDate) > formatDate(b.effectiveDate) ? 1 : 0));
    const past = changes.filter((c) => formatDate(c.effectiveDate) <= today);
    const upcoming = changes.filter((c) => formatDate(c.effectiveDate) > today);
    return { changes, past, upcoming, inEffect: past.length ? past[past.length - 1] : null };
};

export const mergeDocumentTypes = (types) => {
    const seen = new Set();
    return [...DOCUMENT_TYPES, ...(Array.isArray(types) ? types : [])].reduce((list, t) => {
        const name = typeof t === 'string' ? t.trim() : '';
        const key = name.toLowerCase();
        if (name && !seen.has(key)) {
            seen.add(key);
            list.push(name);
        }
        return list;
    }, []);
};

export const DocumentExpiryChip = ({ document }) => {
    const today = toLocalDay();
    const state = documentExpiryState(document, today);
    if (!state) return null;
    const renewal = documentNextRenewal(document, today);
    if (renewal) {
        return (
            <Tooltip title={`Renews automatically every ${renewalPeriodLabel(document.renewalMonths)} · next on ${formatDayShort(renewal)}`}>
                <Chip size="small" label={`Renews ${formatDayShort(renewal)}`} variant="outlined" />
            </Tooltip>
        );
    }
    const expires = formatDate(document.expiresDate);
    const live = !isDocumentCancelled(document, today);
    const action = expiryActionLabel(document.expiryAction);
    const tooltip = [`${state === 'expired' ? 'Expired' : 'Expires'} on ${formatDayShort(expires)}`, action].filter(Boolean).join(' · ');
    const label = live && state === 'expired' ? 'Expired' : `Expires ${formatDayShort(expires)}`;
    const color = live && state === 'expired' ? 'error' : live && state === 'soon' ? 'warning' : 'default';
    return (
        <Tooltip title={tooltip}>
            <Chip size="small" label={label} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />
        </Tooltip>
    );
};

const WHOLE_CLIENT = { type: null, id: null, name: 'Whole client' };

const SCOPE_GROUPS = { Account: 'Accounts', Group: 'Groups', Project: 'Projects' };

const CLIENT_SEARCH_LIMIT = 20;

const scopeKey = (option) => `${option?.type || 'Client'}:${option?.id || ''}`;

const scopeOptionLabel = (option) => {
    if (!option) return '';
    if (!option.type) return option.name;
    if (option.type === 'Account') return `Account: ${option.name || ''}`;
    if (option.type === 'Project' && option.number) return `#${option.number} ${option.name}`;
    return option.name || '';
};

const toScopeOptions = (data) => [
    ...(data?.accounts || []).map((a) => ({ type: 'Account', id: a._id, name: a.name })),
    ...(data?.groups || []).map((g) => ({ type: 'Group', id: g._id, name: g.name, accountName: g.accountName })),
    ...(data?.projects || []).map((p) => ({ type: 'Project', id: p._id, name: p.name, number: p.number, accountName: p.accountName })),
];

const ScopeOptionText = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
});

export const DocumentClientPicker = ({ value, onChange, label = 'Client', placeholder, required = false }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);
    const requestId = useRef(0);

    const search = useCallback(
        (term) => {
            const id = ++requestId.current;
            setLoading(true);
            dispatch(
                getClients(
                    0,
                    CLIENT_SEARCH_LIMIT,
                    (data) => {
                        if (id !== requestId.current) return;
                        setLoading(false);
                        const list = Array.isArray(data) ? data : data?.clients || [];
                        setOptions(list.filter((c) => !c.isDeleted));
                    },
                    term ? { search: term } : {},
                ),
            );
        },
        [dispatch],
    );

    useEffect(() => () => clearTimeout(timer.current), []);

    const handleOpen = () => {
        setOpen(true);
        search(value && input === value.name ? '' : input.trim());
    };

    const handleInput = (_, v, reason) => {
        setInput(v);
        if (reason !== 'input') return;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => search(v.trim()), 300);
    };

    const allOptions = value && !options.some((o) => o._id === value._id) ? [value, ...options] : options;

    return (
        <Autocomplete
            open={open}
            onOpen={handleOpen}
            onClose={() => setOpen(false)}
            options={allOptions}
            filterOptions={(x) => x}
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(o, v) => o?._id === v?._id}
            value={value}
            inputValue={input}
            onInputChange={handleInput}
            onChange={(_, v) => onChange(v)}
            loading={loading}
            renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} required={required} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
        />
    );
};

const FieldsColumn = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
}));

const CurrentFile = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const CurrentFileName = styled(Typography)({
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const FieldPair = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: '1fr 1fr',
    },
}));

const RESPONSIBLE_GROUPS = { staff: 'Staff', client: 'Client users' };

const toResponsibleOption = (u, group) => ({ _id: u._id, fullName: u.fullName || [u.first_name, u.last_name].filter(Boolean).join(' '), email: u.email, group });

const ClientDocumentDialog = ({ open, onClose, onSaved, document: initial, clientId: fixedClientId, pickClient = false }) => {
    const dispatch = useDispatch();
    const isEdit = !!initial?._id;

    const [client, setClient] = useState(null);
    const [type, setType] = useState('');
    const [title, setTitle] = useState('');
    const [scope, setScope] = useState(WHOLE_CLIENT);
    const [scopeOptions, setScopeOptions] = useState([]);
    const [defaultScopeOptions, setDefaultScopeOptions] = useState([]);
    const [scopeInput, setScopeInput] = useState('');
    const [scopesLoading, setScopesLoading] = useState(false);
    const [signed, setSigned] = useState(false);
    const [signedDate, setSignedDate] = useState('');
    const [signer, setSigner] = useState(null);
    const [signerInput, setSignerInput] = useState('');
    const [fetchedUsers, setFetchedUsers] = useState([]);
    const [terms, setTerms] = useState('');
    const [cancelled, setCancelled] = useState(false);
    const [cancelledDate, setCancelledDate] = useState('');
    const [expiresDate, setExpiresDate] = useState('');
    const [expiryAction, setExpiryAction] = useState('');
    const [renewalMonths, setRenewalMonths] = useState('');
    const [responsible, setResponsible] = useState(null);
    const [fetchedStaff, setFetchedStaff] = useState([]);
    const [fetchedTypes, setFetchedTypes] = useState([]);
    const [files, setFiles] = useState([]);
    const [removeFile, setRemoveFile] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadFailed, setUploadFailed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const searchTimer = useRef(null);
    const scopeRequest = useRef(0);
    const scopeSearch = useRef('');

    const clientId = fixedClientId || initial?.client?._id || client?._id || null;
    const showClientPicker = !fixedClientId && !isEdit && pickClient;

    const signerOptions = useMemo(() => fetchedUsers.filter((u) => u && u.active !== false), [fetchedUsers]);

    const typeOptions = useMemo(() => mergeDocumentTypes(fetchedTypes), [fetchedTypes]);

    const responsibleOptions = useMemo(() => {
        const seen = new Set();
        const list = [...fetchedStaff.map((u) => toResponsibleOption(u, RESPONSIBLE_GROUPS.staff)), ...signerOptions.map((u) => toResponsibleOption(u, RESPONSIBLE_GROUPS.client))].filter((u) => {
            const key = String(u._id);
            if (!u._id || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        if (responsible?._id && !seen.has(String(responsible._id))) list.unshift(responsible);
        return list;
    }, [fetchedStaff, signerOptions, responsible]);

    useEffect(() => {
        if (!open) return;
        setClient(null);
        setType(initial?.document || '');
        setTitle(initial?.title || '');
        setScope(initial?.scope?.type ? { type: initial.scope.type, id: initial.scope.id, name: initial.scope.name } : WHOLE_CLIENT);
        setScopeInput('');
        setSigned(!!initial?.signed);
        setSignedDate(formatDate(initial?.signedDate));
        const sb = initial?.signedBy && typeof initial.signedBy === 'object' ? initial.signedBy : null;
        setSigner(sb);
        setSignerInput(sb?.fullName || initial?.signedByName || '');
        setTerms(initial?.terms || '');
        setCancelled(!!initial?.cancelledDate);
        setCancelledDate(formatDate(initial?.cancelledDate));
        setExpiresDate(formatDate(initial?.expiresDate));
        setExpiryAction(initial?.expiryAction || '');
        setRenewalMonths(initial?.renewalMonths || '');
        const rp = initial?.responsible && typeof initial.responsible === 'object' && initial.responsible._id ? initial.responsible : null;
        setResponsible(rp ? toResponsibleOption(rp, '') : null);
        setFiles([]);
        setRemoveFile(false);
        setIsUploading(false);
        setUploadFailed(false);
        setSubmitting(false);
    }, [open, initial]);

    const fetchScopes = (search) => {
        if (!clientId) return;
        const id = ++scopeRequest.current;
        scopeSearch.current = search;
        setScopesLoading(true);
        dispatch(
            getClientDocumentScopes(clientId, search, (data) => {
                if (id !== scopeRequest.current) return;
                setScopesLoading(false);
                if (!data) return;
                const options = toScopeOptions(data);
                setScopeOptions(options);
                if (!search) setDefaultScopeOptions(options);
                if (Array.isArray(data.users)) setFetchedUsers(data.users);
                if (Array.isArray(data.staff)) setFetchedStaff(data.staff);
            }),
        );
    };

    useEffect(() => {
        scopeRequest.current += 1;
        setScopeOptions([]);
        setDefaultScopeOptions([]);
        setFetchedUsers([]);
        setFetchedStaff([]);
        setScopesLoading(false);
        if (!open || !clientId) return;
        fetchScopes('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clientId]);

    useEffect(() => () => clearTimeout(searchTimer.current), []);

    useEffect(() => {
        if (!open) return undefined;
        let live = true;
        dispatch(
            getClientDocumentTypes((data) => {
                if (live && Array.isArray(data?.types)) setFetchedTypes(data.types);
            }),
        );
        return () => {
            live = false;
        };
    }, [open, dispatch]);

    const showDefaultScopes = () => {
        clearTimeout(searchTimer.current);
        if (scopeSearch.current) {
            scopeRequest.current += 1;
            scopeSearch.current = '';
            setScopesLoading(false);
        }
        setScopeOptions(defaultScopeOptions);
    };

    const handleScopeInput = (_, value, reason) => {
        setScopeInput(value);
        if (reason === 'reset') {
            showDefaultScopes();
            return;
        }
        if (reason !== 'input') return;
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => fetchScopes(value.trim()), 300);
    };

    const handleScopeOpen = () => {
        const current = scopeInput.trim();
        if (!current || current === scopeOptionLabel(scope)) showDefaultScopes();
    };

    const filterScopeOptions = (options, { inputValue }) => {
        const needle = inputValue.trim().toLowerCase();
        if (!needle || needle === scopeOptionLabel(scope).toLowerCase()) return options;
        return options.filter((o) => scopeOptionLabel(o).toLowerCase().includes(needle));
    };

    const renderScopeOption = (props, option) => (
        <li {...props} key={scopeKey(option)}>
            <ScopeOptionText>
                <Typography variant="body2">{scopeOptionLabel(option)}</Typography>
                {option.accountName && (
                    <Typography variant="caption" color="text.secondary">
                        {option.accountName}
                    </Typography>
                )}
            </ScopeOptionText>
        </li>
    );

    const allScopeOptions = useMemo(() => {
        const list = [...scopeOptions];
        if (scope?.type && !list.some((o) => o.type === scope.type && o.id === scope.id)) {
            const order = Object.keys(SCOPE_GROUPS);
            const at = list.findIndex((o) => order.indexOf(o.type) > order.indexOf(scope.type));
            list.splice(at === -1 ? list.length : at, 0, scope);
        }
        return [WHOLE_CLIENT, ...list];
    }, [scopeOptions, scope]);

    const handleClientChange = (value) => {
        setClient(value);
        setScope(WHOLE_CLIENT);
        setSigner(null);
        setSignerInput('');
        setResponsible(null);
    };

    const handleSignerInput = (_, value, reason) => {
        setSignerInput(value);
        if (reason === 'input' && signer && typeof signer === 'object' && value !== signer.fullName) setSigner(null);
    };

    const recordOnly = isRecordOnlyDocument(type);
    const hasCurrentFile = isEdit && !!initial?.attachment && !removeFile;
    const showUploader = !recordOnly && !hasCurrentFile;
    const hasChangeFiles = isEdit && Array.isArray(initial?.changes) && initial.changes.some((c) => !!c?.attachment);
    const fileBlocksSave = recordOnly && hasCurrentFile;
    const changeFilesBlockSave = recordOnly && hasChangeFiles;
    const autoRenews = expiryAction === AUTO_RENEW_ACTION;
    const expiryMissing = !!expiryAction && !expiresDate;
    const renewalMissing = autoRenews && !renewalMonths;
    const canSubmit =
        !!type.trim() &&
        !!clientId &&
        !submitting &&
        !(showUploader && (isUploading || uploadFailed)) &&
        !fileBlocksSave &&
        !changeFilesBlockSave &&
        !(cancelled && !cancelledDate) &&
        !expiryMissing &&
        !renewalMissing;

    useEffect(() => {
        if (showUploader) return;
        setFiles([]);
        setIsUploading(false);
        setUploadFailed(false);
    }, [showUploader]);

    const handleCancelledChange = (e) => {
        const next = e.target.checked;
        setCancelled(next);
        if (next) setCancelledDate((current) => current || toLocalDay());
    };

    const handleSubmit = () => {
        if (!canSubmit) return;
        const selectedUser = signer && typeof signer === 'object' && signerInput === signer.fullName ? signer : null;
        const uploads = showUploader ? restoreAttachments(files) : [];
        const payload = {
            document: type.trim(),
            title: title.trim(),
            terms,
            signed,
            signedDate: (signed && signedDate) || null,
            signedBy: signed && selectedUser ? selectedUser._id : null,
            signedByName: signed && !selectedUser ? signerInput.trim() : '',
            scope: scope?.type ? { type: scope.type, id: scope.id } : null,
            attachments: uploads,
            cancelledDate: cancelled ? cancelledDate : null,
            expiresDate: expiresDate || null,
            expiryAction: expiryAction || null,
            renewalMonths: autoRenews ? Number(renewalMonths) : null,
            responsible: responsible?._id || null,
        };
        if (isEdit && removeFile && uploads.length === 0) payload.removeFile = true;
        setSubmitting(true);
        const onDone = (result) => {
            setSubmitting(false);
            if (!result) return;
            onSaved?.(result);
        };
        if (isEdit) {
            dispatch(updateClientDocument(clientId, initial._id, payload, onDone));
        } else {
            dispatch(addClientDocument(clientId, payload, onDone));
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? 'Edit Document' : 'Add Document'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    {recordOnly ? 'Record a signed agreement.' : 'Record a signed agreement and attach the signed copy.'}
                </Typography>
                <FieldsColumn>
                    {showClientPicker && <DocumentClientPicker value={client} onChange={handleClientChange} placeholder="Search clients" required />}
                    <Autocomplete
                        freeSolo
                        options={typeOptions}
                        value={type}
                        inputValue={type}
                        onChange={(_, v) => setType(v || '')}
                        onInputChange={(_, v) => setType(v || '')}
                        renderInput={(params) => <TextField {...params} label="Type" required />}
                    />
                    <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} helperText="What it covers, e.g. Retainer - acme.com" />
                    <Autocomplete
                        options={allScopeOptions}
                        value={scope}
                        inputValue={scopeInput}
                        onChange={(_, v) => setScope(v || WHOLE_CLIENT)}
                        onInputChange={handleScopeInput}
                        onOpen={handleScopeOpen}
                        filterOptions={filterScopeOptions}
                        groupBy={(o) => SCOPE_GROUPS[o.type] || ''}
                        getOptionLabel={scopeOptionLabel}
                        renderOption={renderScopeOption}
                        isOptionEqualToValue={(o, v) => o.type === v?.type && o.id === v?.id}
                        loading={scopesLoading}
                        disabled={!clientId}
                        disableClearable
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Applies to"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {scopesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <FormControlLabel control={<Switch checked={signed} onChange={(e) => setSigned(e.target.checked)} />} label="Signed" />
                    {signed && (
                        <>
                            <AppDatePicker label="Signed Date" value={signedDate} onChange={(v) => setSignedDate(v)} />
                            <Autocomplete
                                freeSolo
                                options={signerOptions}
                                getOptionLabel={(o) => (typeof o === 'string' ? o : o?.fullName || '')}
                                isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                value={signer}
                                inputValue={signerInput}
                                onChange={(_, v) => setSigner(v)}
                                onInputChange={handleSignerInput}
                                disabled={!clientId}
                                renderInput={(params) => <TextField {...params} label="Signed By" helperText="Pick a client user or type a name" />}
                            />
                        </>
                    )}
                    {hasCurrentFile && (
                        <Box>
                            <CurrentFile>
                                <InsertDriveFileIcon fontSize="small" color="action" />
                                <CurrentFileName variant="body2">{initial.attachment.name}</CurrentFileName>
                                <Button size="small" color="error" onClick={() => setRemoveFile(true)}>
                                    Remove
                                </Button>
                            </CurrentFile>
                            {fileBlocksSave && <FormHelperText error>Remove the file to save this as a Credit Card Authorization</FormHelperText>}
                        </Box>
                    )}
                    {showUploader && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Signed copy (PDF or image)
                            </Typography>
                            <FilePondUploader
                                setAttachments={setFiles}
                                onUploadStatusChange={setIsUploading}
                                onErrorStateChange={setUploadFailed}
                                allowMultiple={false}
                                acceptedFileTypes={DOCUMENT_FILE_TYPES}
                            />
                            {uploadFailed && <FormHelperText error>Remove the failed file or upload it again</FormHelperText>}
                        </Box>
                    )}
                    {changeFilesBlockSave && <FormHelperText error>Remove the files on this document's changes to save it as a Credit Card Authorization</FormHelperText>}
                    <TextField label="Agreement terms" value={terms} onChange={(e) => setTerms(e.target.value)} multiline minRows={3} maxRows={12} />
                    <FieldPair>
                        <AppDatePicker
                            label="Expires"
                            value={expiresDate}
                            onChange={(v) => setExpiresDate(v)}
                            slotProps={{
                                field: { clearable: true },
                                actionBar: ({ wrapperVariant }) => ({ actions: wrapperVariant === 'desktop' ? [] : ['clear', 'cancel', 'accept'] }),
                            }}
                        />
                        <TextField select fullWidth label="At expiry" value={expiryAction} onChange={(e) => setExpiryAction(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                            <MenuItem value="">Not set</MenuItem>
                            {EXPIRY_ACTIONS.map((a) => (
                                <MenuItem key={a.value} value={a.value}>
                                    {a.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FieldPair>
                    {expiryMissing && <FormHelperText error>Set an expiry date to use an At expiry action</FormHelperText>}
                    {autoRenews && (
                        <TextField select fullWidth required label="Renews every" value={renewalMonths} onChange={(e) => setRenewalMonths(e.target.value)} error={renewalMissing} helperText={renewalMissing ? 'Choose how often it renews' : ''}>
                            {RENEWAL_PERIODS.map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                    {p.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <Autocomplete
                        options={responsibleOptions}
                        groupBy={(o) => o.group || ''}
                        getOptionLabel={(o) => o?.fullName || ''}
                        isOptionEqualToValue={(o, v) => String(o?._id) === String(v?._id)}
                        value={responsible}
                        onChange={(_, v) => setResponsible(v)}
                        disabled={!clientId}
                        renderInput={(params) => <TextField {...params} label="Responsible" />}
                    />
                    <FormControlLabel control={<Switch checked={cancelled} onChange={handleCancelledChange} />} label="Cancelled" />
                    {cancelled && <AppDatePicker label="Cancelled Date" value={cancelledDate} onChange={(v) => setCancelledDate(v)} required />}
                </FieldsColumn>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
                    {isEdit ? 'Save Changes' : 'Add Document'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const ClientDocumentChangeDialog = ({ open, onClose, onSaved, clientId, document: parent, change }) => {
    const dispatch = useDispatch();
    const isEdit = !!change?._id;
    const hasPermission = useHasPermission();
    const canSeePrivate = hasPermission('canSeePrivate');
    const [effectiveDate, setEffectiveDate] = useState('');
    const [summary, setSummary] = useState('');
    const [privacy, setPrivacy] = useState('Public');
    const [files, setFiles] = useState([]);
    const [removeFile, setRemoveFile] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadFailed, setUploadFailed] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setEffectiveDate(formatDate(change?.effectiveDate) || toLocalDay());
        setSummary(change?.summary || '');
        setPrivacy(change?.privacy || 'Public');
        setFiles([]);
        setRemoveFile(false);
        setIsUploading(false);
        setUploadFailed(false);
        setSubmitting(false);
    }, [open, change]);

    const recordOnly = isRecordOnlyDocument(parent?.document);
    const hasCurrentFile = isEdit && !!change?.attachment && !removeFile;
    const showUploader = !recordOnly && !hasCurrentFile;
    const fileBlocksSave = recordOnly && hasCurrentFile;
    const canSubmit = !!clientId && !!parent?._id && !!effectiveDate && !!summary.trim() && !submitting && !(showUploader && (isUploading || uploadFailed)) && !fileBlocksSave;

    const handleSubmit = () => {
        if (!canSubmit) return;
        const uploads = showUploader ? restoreAttachments(files) : [];
        const payload = { effectiveDate, summary: summary.trim(), privacy, attachments: uploads };
        if (isEdit && removeFile && uploads.length === 0) payload.removeFile = true;
        setSubmitting(true);
        const onDone = (result) => {
            setSubmitting(false);
            if (!result) return;
            onSaved?.(result);
        };
        if (isEdit) {
            dispatch(updateClientDocumentChange(clientId, parent._id, change._id, payload, onDone));
        } else {
            dispatch(addClientDocumentChange(clientId, parent._id, payload, onDone));
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? 'Edit Change' : 'Add Change'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    Record a dated change to this document.
                </Typography>
                <FieldsColumn>
                    <AppDatePicker label="Effective date" value={effectiveDate} onChange={(v) => setEffectiveDate(v)} required />
                    <TextField label="Summary" value={summary} onChange={(e) => setSummary(e.target.value)} multiline minRows={3} maxRows={12} required placeholder="e.g. Retainer moves from 30 to 10 hours" />
                    <TextField select fullWidth label="Privacy" value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
                        <MenuItem value="Public">Public</MenuItem>
                        <MenuItem value="Internal">Internal</MenuItem>
                        {canSeePrivate && <MenuItem value="Private">Private</MenuItem>}
                    </TextField>
                    {hasCurrentFile && (
                        <Box>
                            <CurrentFile>
                                <InsertDriveFileIcon fontSize="small" color="action" />
                                <CurrentFileName variant="body2">{change.attachment.name}</CurrentFileName>
                                <Button size="small" color="error" onClick={() => setRemoveFile(true)}>
                                    Remove
                                </Button>
                            </CurrentFile>
                            {fileBlocksSave && <FormHelperText error>Remove the file to save a change on a Credit Card Authorization</FormHelperText>}
                        </Box>
                    )}
                    {showUploader && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Signed amendment (PDF or image, optional)
                            </Typography>
                            <FilePondUploader
                                setAttachments={setFiles}
                                onUploadStatusChange={setIsUploading}
                                onErrorStateChange={setUploadFailed}
                                allowMultiple={false}
                                acceptedFileTypes={DOCUMENT_FILE_TYPES}
                            />
                            {uploadFailed && <FormHelperText error>Remove the failed file or upload it again</FormHelperText>}
                        </Box>
                    )}
                </FieldsColumn>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
                    {isEdit ? 'Save Changes' : 'Add Change'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClientDocumentDialog;
